import React from 'react';
import {
  ButtonGroup,
  ButtonIcon,
  Card,
  CardBody,
  Table,
} from '@statsbomb/kitbag-components';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { FormattedMessage, useIntl } from 'react-intl';
import { useTheme } from 'styled-components';
import {
  REACT_APP_AUTH_ROLE_ANALYST,
  REACT_APP_AUTH_ROLE_COLLECTOR,
  REACT_APP_AUTH_ROLE_OPERATOR,
} from '../../AuthProvider';
import {
  alignmentNotesAtom,
  draftIsAlignedAtom,
  playbackAndMinutesOkAtom,
} from '../../atoms/videoAlignmentCheck';
import { videoReferenceSelectedForAlignmentCheckAtom } from '../../atoms/videoAlignmentRequired';
import {
  updateVideoSearchResultsAtom,
  videoSearchResultsAtom,
} from '../../atoms/videoListings';
import { MEDIA_SOURCE } from '../../constants/source';
import { useVideoDataForAlignmentCheck } from '../../hooks/loadVideosRequiringAlignmentCheck';
import { auditDataDeserialiser } from '../../serialisers/auditData';
import { qualityAssuranceSerialiser } from '../../serialisers/qualityAsssurance';
import { sourceDeserialiser } from '../../serialisers/sourceData';
import BooleanDisplay from '../BooleanDisplay';
import CardHeader from '../CardHeader';
import CellHeadings from '../CellHeadings';
import CopyToClipboardWithIcon from '../Copy';
import { VALUE_IDENTIFYING_NOT_SHOWN } from '../OffsetData/OffsetData';
import Period from '../Period';
import { PreviewPlayer } from '../PreviewPlayer/PreviewPlayer';
import ReviewLibraryEntry from '../ReviewLibraryEntry';
import Secured from '../Secured';
import VideoAlignmentCheck from '../VideoAlignmentCheck';
import VideoInfo from '../VideoInfo';
import { getAlignmentStatus, getHeadings } from './VideoDisplay.helper';
import { isNotNullOrUndefined } from '../../utils/funcs';
import { getAlignmentOutcome } from '../../utils/alignment-data';

export function VideosDisplay() {
  const intl = useIntl();
  const theme = useTheme();

  const videos = useAtomValue(videoSearchResultsAtom);
  const [selectedVideoReference, setSelectedVideoReference] = useAtom(
    videoReferenceSelectedForAlignmentCheckAtom,
  );
  const isSmallScreen = !theme.mqMatch(theme.mqFrom('lg'));
  const headings = getHeadings(intl, isSmallScreen);

  const isAligned = useAtomValue(draftIsAlignedAtom);
  const playbackAndMinutesOk = useAtomValue(playbackAndMinutesOkAtom);
  const alignmentNotes = useAtomValue(alignmentNotesAtom);
  const updateVideoSearchResults = useSetAtom(updateVideoSearchResultsAtom);

  useVideoDataForAlignmentCheck(videos);

  const alignmentOutcome = getAlignmentOutcome(playbackAndMinutesOk);

  const handleModalFinalConfirm = () => {
    updateVideoSearchResults(selectedVideoReference, {
      ...(isNotNullOrUndefined(playbackAndMinutesOk) && {
        alignment_issue: alignmentOutcome.alignmentIssue,
        alignment_status: alignmentOutcome.alignmentStatus,
      }),
      alignment_notes: alignmentNotes,
      is_aligned: isAligned,
    });
  };

  return (
    <>
      <Card>
        <CardHeader>
          <FormattedMessage
            defaultMessage="Requested Files"
            description="Requested Files"
            id="75cee"
          />
        </CardHeader>
        <CardBody>
          <Table size="small" aria-label="Uploads">
            <CellHeadings cells={headings} textAlign="left" />
            <Table.Body>
              {videos.length < 1 ? (
                <Table.Row>
                  <Table.DataCell colSpan={headings.length}>
                    <FormattedMessage
                      defaultMessage="Empty video listing"
                      id="f8a58"
                    />
                  </Table.DataCell>
                </Table.Row>
              ) : (
                videos.map((video) => {
                  const videoReference = {
                    matchId: video.match_id,
                    period: video.match_part,
                    sport: video.sport,
                    track: video.track,
                    version: video.version,
                  };

                  const alignmentStatus = getAlignmentStatus(video, intl);

                  const auditData = auditDataDeserialiser(
                    video.video_info_audit,
                  );

                  const qualityAssuranceInfo = qualityAssuranceSerialiser(
                    video.quality_assurance_info,
                  );

                  const sourceData = sourceDeserialiser(video.source_data);

                  const missingMinutesString = (
                    video.kickoff === VALUE_IDENTIFYING_NOT_SHOWN ||
                    video.period_end === VALUE_IDENTIFYING_NOT_SHOWN
                  ).toString();

                  const url = video.presigned_url || video.stream_url;

                  return (
                    <Table.Row
                      key={`${Object.values(videoReference).join('-')}-row`}
                    >
                      <Table.DataCell $textAlign="left">
                        <Period period={video.match_part} sport={video.sport} />
                      </Table.DataCell>
                      <Table.DataCell $textAlign="left">
                        <VideoInfo listing={video} />
                      </Table.DataCell>
                      {!isSmallScreen && (
                        <>
                          <Table.DataCell
                            $textAlign="left"
                            data-testid="video-source"
                          >
                            {video?.source_data?.source_name ===
                            MEDIA_SOURCE.GSL ? (
                              <FormattedMessage
                                defaultMessage="GSL Video"
                                id="986d5"
                              />
                            ) : (
                              <FormattedMessage
                                defaultMessage="Non GSL Video"
                                id="a2432"
                              />
                            )}
                          </Table.DataCell>
                          <Table.DataCell
                            $textAlign="left"
                            data-testid="do-not-use"
                          >
                            <BooleanDisplay value={video.do_not_use} />
                          </Table.DataCell>
                          <Table.DataCell
                            $textAlign="left"
                            data-testid="is-licensed"
                          >
                            <BooleanDisplay
                              value={video.source_data?.is_licensed}
                            />
                          </Table.DataCell>
                          <Table.DataCell
                            $textAlign="left"
                            data-testid="alignment-status"
                          >
                            {alignmentStatus}
                          </Table.DataCell>
                          <Table.DataCell
                            $textAlign="left"
                            data-testid="missing-minutes"
                          >
                            <BooleanDisplay value={missingMinutesString} />
                          </Table.DataCell>
                        </>
                      )}
                      <Table.DataCell $textAlign="left">
                        <ButtonGroup>
                          <Secured
                            allowed={[
                              REACT_APP_AUTH_ROLE_COLLECTOR,
                              REACT_APP_AUTH_ROLE_ANALYST,
                            ]}
                          >
                            <PreviewPlayer
                              key={`${video.e_tag}-preview`}
                              size="small"
                              url={video.presigned_url}
                            />
                          </Secured>
                          <Secured allowed={REACT_APP_AUTH_ROLE_OPERATOR}>
                            <ReviewLibraryEntry
                              alignmentStatus={alignmentStatus}
                              auditData={auditData}
                              isDoNotUse={video.do_not_use}
                              matchId={video.match_id}
                              metadata={video.metadata}
                              part={video.match_part}
                              periodEnd={video.period_end}
                              periodStart={video.kickoff}
                              qualityAssuranceInfo={qualityAssuranceInfo}
                              size="small"
                              sourceData={sourceData}
                              sport={video.sport}
                              track={video.track}
                              url={url}
                              version={video.version}
                              /* eslint-disable react/jsx-props-no-spreading */
                              {...(!video.presigned_url && {
                                gslCredentialsRequired:
                                  video.gsl_credentials_required,
                                misalignmentIssue: video.alignment_issue,
                                misalignmentStatus: video.alignment_status,
                              })}
                            />
                          </Secured>
                          {video.presigned_url && (
                            <>
                              <CopyToClipboardWithIcon
                                size="small"
                                text={video.presigned_url}
                              />
                              <a href={video.presigned_url}>
                                <ButtonIcon
                                  icon="Download"
                                  size="small"
                                  title={intl.formatMessage({
                                    defaultMessage: 'Download',
                                    id: '801ab',
                                  })}
                                  variant="secondary"
                                />
                              </a>
                            </>
                          )}
                          {!video.presigned_url && (
                            <ButtonIcon
                              icon="Edit"
                              size="small"
                              variant="secondary"
                              title={intl.formatMessage({
                                defaultMessage: 'Edit',
                                id: '7dce1',
                              })}
                              onClick={() =>
                                setSelectedVideoReference(videoReference)
                              }
                            />
                          )}
                        </ButtonGroup>
                      </Table.DataCell>
                      {!isSmallScreen && (
                        <Table.DataCell
                          $textAlign="left"
                          data-testid="last-updated"
                        >
                          {intl.formatTime(
                            video.video_info_audit?.last_updated_at ||
                              video.last_modified,
                            {
                              day: 'numeric',
                              month: 'numeric',
                              year: 'numeric',
                            },
                          )}
                        </Table.DataCell>
                      )}
                    </Table.Row>
                  );
                })
              )}
            </Table.Body>
          </Table>
        </CardBody>
      </Card>
      <VideoAlignmentCheck onFinalConfirm={handleModalFinalConfirm} />
    </>
  );
}

export default { VideosDisplay };
